import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../../core/modules/global';
import { UPDATE_CATEGORY_WITH_LIMIT_DIALOG } from '../../../../../core/constants/dialog';
import Spinner from '../../../Spinner';
import { tType, useMasterCore } from '../../../../../core/modules/master';
import { useCompanyCore } from '../../../../../core/modules/company';
import Swal from 'sweetalert2';
import { Type } from '../../../../../core/interfaces/type.interface';
import { ContainerCategory, ProductCategory } from '../../../../../core/interfaces/category.interface';
import { TypeLabel } from './CreateDialog';

interface Props {
  type: tType;
  types: Type[];
  parent_id: number | null;
  onClose: () => void;
}

interface PostCategory {
  acronym: string;
  name: string;
  description: string;
  type_id: number;
}

const schema = yup.object().shape({
  acronym: yup.string().test('len', '3 carácteres requeridos', val => val?.length === 3).required('Campo requerido'),
  name: yup.string().required('Campo requerido'),
  description: yup.string().required('Campo requerido'),
  type_id: yup.number().required('Campo requerido'),
});

const UpdateDialogWithLimit = (props: Props) => {
  const { type, types, parent_id, onClose } = props;
  const { dialogs, closeDialog } = useGlobalCore();
  const { category, putCategory, deleteCategory, getCategories, setCategory } = useMasterCore();
  const { companyId } = useCompanyCore();

  const [loading, setLoading]  = useState(false);

  const { control, formState, handleSubmit, resetField, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  const resetForm = useCallback(() => {
    resetField('acronym');
    resetField('name');
    resetField('description');
    resetField('type_id');
    onClose();
    setCategory(null);
    closeDialog(UPDATE_CATEGORY_WITH_LIMIT_DIALOG);
  }, [onClose, closeDialog, resetField, setCategory]);

  useEffect(() => {
    if (category) {
      setValue("acronym", (category as ContainerCategory).acronym);
      setValue("name", category.name);
      setValue("description", category.description);
      if (type === 'product') {
        setValue("type_id", (category as ProductCategory).product_type_id);
      } else {
        setValue("type_id", (category as ContainerCategory).container_type_id);
      }
    }
  }, [category, type, setValue]);

  const onSubmit = useCallback(
    (values: PostCategory) => {
      if (companyId && category) {
        setLoading(true);
        const body = type === 'product' ? {
          ...values,
          parent_id,
          product_type_id: values.type_id,
        } : {
          ...values,
          parent_id,
          container_type_id: values.type_id,
        };

        putCategory(companyId, category.id, body, type, () => {
          resetForm();
          getCategories(companyId, type);
          setLoading(false);
        }, () => {
          setLoading(false);
        });
      }
    },
    [companyId, category, type, parent_id, putCategory, resetForm, getCategories]
  );

  const onDelete = useCallback(() => {
    Swal.fire({
      title: "Confimar acción",
      text: `Eliminar la categoría ${category?.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, eliminar!",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed && companyId && category) {
        setLoading(true);

        deleteCategory(
          companyId,
          category.id,
          type,
          () => {
            setLoading(false);
            getCategories(companyId, type);
            resetForm();
          },
          () => {
            setLoading(false);
          }
        );
      }
    });
  }, [category, companyId, type, deleteCategory, getCategories, resetForm]);

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(UPDATE_CATEGORY_WITH_LIMIT_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Modificar Categoría {TypeLabel[type]}
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="acronym"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Sigla"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.acronym}
                          helperText={errors?.acronym?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.name}
                          helperText={errors?.name?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Descripción"
                          type="textarea"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.description}
                          helperText={errors?.description?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="type_id"
                      control={control}
                      render={({ field }) => (
                        <FormControl variant="outlined" fullWidth>
                          <InputLabel id="type-label">
                            Tipo
                          </InputLabel>
                          <Select
                            {...field}
                            labelId="type-label"
                            label="Tipo"
                            disabled={loading}
                            required
                          >
                            {types.map((type) => (
                              <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </div>

                  <div className="flex justify-center gap-4">
                    <Button
                      variant="contained"
                      color="error"
                      className="w-full"
                      sx={{ borderRadius: 50 }}
                      disabled={loading}
                      type="button"
                      onClick={onDelete}
                    >
                      <Spinner show={loading} />
                      Eliminar
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      className="w-full"
                      sx={{ borderRadius: 50 }}
                      disabled={!isEmpty(errors) || !isValid || loading}
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Guardar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateDialogWithLimit;
